import { useTranslation } from 'react-i18next'

import { Body2 } from '@ui/Typography'
import { RichIcon } from '@ui/components/RichIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { InfoFilledIcon } from '@ui/icons/InfoFilled.icon'

import { Box } from '@shared/components/Box'

import { MaskedDateInput } from './DateRangeInput'
import {
  DateRangeParserReturnType,
  getDateRangeFormatConfig,
  getDateRangeInputEndError,
  getDateRangeInputStartError,
} from './utils'

export type DateRangeFormatConfig = {
  placeholder: string
  format: (date: Date) => string
  parse: (value: string) => DateRangeParserReturnType
  imaskPattern: string
}

interface DateRangeFromToProps {
  startValue: string
  endValue: string
  startDate?: Date
  endDate?: Date
  dateFormat?: string
  minDate?: Date
  maxDate?: Date
  onStartChange: (value: string, date?: Date) => void
  onEndChange: (value: string, date?: Date) => void
}

export const DateRangeFromTo = ({
  startValue,
  endValue,
  startDate,
  dateFormat,
  minDate,
  maxDate,
  onStartChange,
  onEndChange,
}: DateRangeFromToProps) => {
  const config = getDateRangeFormatConfig(dateFormat)
  const { t } = useTranslation()

  const startError = getDateRangeInputStartError({ value: startValue, config, minDate, maxDate, t })
  const endError = getDateRangeInputEndError({
    value: endValue,
    config,
    startDate,
    minDate,
    maxDate,
    t,
  })
  const sharedError = endError ?? startError

  return (
    <>
      <Box flex align='center' justify='center' gap='x16'>
        <MaskedDateInput
          value={startValue}
          config={config}
          onChange={onStartChange}
          error={startError}
          ariaLabel={t('Calendar.Start date')}
        />
        <RichIcon
          size='x20'
          icon={{ icon: ArrowIcon.Right, color: 'text-low-em' }}
          shape='circle'
        />
        <MaskedDateInput
          value={endValue}
          config={config}
          onChange={onEndChange}
          error={endError}
          ariaLabel={t('Calendar.End date')}
        />
      </Box>
      {sharedError && (
        <Box flex gap='x4' align='center' mt='x4'>
          <InfoFilledIcon color='text-danger-med-em' size={18} />
          <Body2 color='text-danger-med-em'>{sharedError}</Body2>
        </Box>
      )}
    </>
  )
}
